/** @jsxRuntime automatic */
/** @jsxImportSource react */
import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import {
  aggregateDashboardKpi,
  dashboardKpiDataStatus,
  dashboardKpiRequestKey,
  resolveDashboardKpi,
} from "@/datox/dashboard-kpi"
import { KpiComparisonDisplay } from "./kpi-comparison"
import { WidgetEmpty } from "./WidgetEmpty"
import { KPI_COMPOSITION, KPI_VALUE_FIT, kpiShowsSublabel, kpiSublabelLines, kpiValueSize } from "./tokens/kpi"
import { DashboardFitContext, kpiRowSize } from "./dashboard-fit-context"
import { kpiResult } from "./advanced-widget-data"

const KPI_FORMAT_ALIASES = { float: "number", decimal: "number", auto: "number", int: "integer" }

/** @type {import("react").Context<{
 * datasets?: Record<string, import("@/datox/dashboard-kpi").DashboardKpiDataset>,
 * locale?: string, sourceKey?: string, dashboardID?: string,
 * sourceKeys?: Record<string, string>,
 * dataDate?: Date, v2?: boolean,
 * kpiValues?: Record<string, import("@/datox/api").DatoxKpiResult>,
 * onEvaluateKpi?: (widget: Record<string, unknown>, filters: Array<Record<string, unknown>>, evidenceLimit: number) => Promise<import("@/datox/api").DatoxKpiResult>
 * }>} */
export const DashboardKpiContext = createContext({ datasets: {} })

// Bobur's KPI display (leshabank 37f2a2b) lives outside `style`: `kpi_display` {layout, format, decimals, notation,
// currency, unit, percent_scale, target}, `kpi_display_scale` and `kpi_prefix`. Our inspector writes the same
// settings into `style`. Read both: his keys win while present (his agent and classic editor write them); our
// inspector folds them into `style` on its first edit (`foldKpiDisplay`).
export function kpiDisplayStyle(widget) {
  const style = widget?.style || {}
  const display = widget?.kpi_display
  const mapped = {}
  if (display && typeof display === "object") {
    if (display.layout) mapped.kpiLayout = display.layout
    if (display.format && display.format !== "inherit") {
      mapped.valueFormat = display.format
      if (Number.isInteger(display.decimals)) mapped.decimalPlaces = display.decimals
    }
    if (display.notation) mapped.notation = display.notation
    if (display.currency) mapped.currency = display.currency
    if (display.unit) mapped.unit = display.unit
    if (display.percent_scale) mapped.percentScale = display.percent_scale
    if (display.target !== undefined) mapped.target = display.target
  }
  if (widget?.kpi_display_scale && widget.kpi_display_scale !== "none") mapped.scale = widget.kpi_display_scale
  if (typeof widget?.kpi_prefix === "string" && widget.kpi_prefix.trim()) mapped.prefix = widget.kpi_prefix.trim()
  return { ...style, ...mapped }
}

// Our inspector's patch for a KPI display change: his keys folded into `style` and removed, so one place holds
// the setting from then on (his prefix stays in `kpi_prefix`, which his validator owns).
export function foldKpiDisplay(widget, patch) {
  const { prefix: _prefix, ...style } = kpiDisplayStyle(widget)
  return { style: { ...style, ...patch }, kpi_display: undefined, kpi_display_scale: undefined }
}

// His prefix ("$", "≈") before the number, after a minus sign; not on currency or percent values.
export function withKpiPrefix(text, style) {
  const prefix = style?.prefix
  if (!prefix || !text || style.valueFormat === "currency" || style.valueFormat === "percent") return text
  return text.startsWith("-") ? `-${prefix}${text.slice(1)}` : `${prefix}${text}`
}

export function kpiIssueMessage(labels, error) {
  const template = labels.errors?.[error?.code] || labels.errors?.invalid_expression || labels.error
  return template.replace(/\{(\w+)\}/g, (_, key) => String(error?.params?.[key] ?? ""))
}

export function KpiWidget(props) {
  const element = useRef(null)
  const [size, setSize] = useState({ width: 0, height: 0, box: 0, textWidth: 0, numberWidth: 0 })
  const labels = props.labels.kpi
  const [remote, setRemote] = useState(null)
  const canEvaluate = !!(props.onEvaluateKpi && props.widget?.id)
  const requestKey = dashboardKpiRequestKey(props.widget || {}, props.filterWidgets || [], props.sourceKey)
  useEffect(() => {
    if (!canEvaluate || !props.widget?.data?.dataset_id || props.status !== "ready") return undefined
    // An unrelated resource reload must not clear or reevaluate the same result.
    if (remote?.key === requestKey && remote.value.status !== "error") return undefined
    let active = true
    setRemote(null)
    const timer = setTimeout(() => {
      props
        .onEvaluateKpi(props.widget, props.filterWidgets || [], 0)
        .then((value) => {
          if (active) setRemote({ key: requestKey, value })
        })
        .catch(() => {
          if (active) setRemote({ key: requestKey, value: { status: "error" } })
        })
    }, 120)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [requestKey, props.status, canEvaluate])
  const calculated =
    canEvaluate && (props.status === "ready" || props.status === "loading")
      ? remote?.key === requestKey
        ? remote.value
        : !props.filterWidgets?.length
          ? props.captured
          : null
      : null
  // No server evaluator (leshabank has none for our KPI endpoint): a formula KPI runs on Bobur's client
  // interpreter (advanced-widget-data.js, the same rules as his backend; it never executes code).
  const localFormula =
    !canEvaluate && props.widget?.kpi_calculation && props.status === "ready" ? kpiResult(props.widget, props.table) : null
  const status =
    localFormula
      ? localFormula.status === "ready" && typeof localFormula.value === "number"
        ? "ready"
        : localFormula.status === "error"
          ? "error"
          : "missing"
      : calculated && canEvaluate
      ? calculated.status === "unavailable"
        ? "unavailable"
        : calculated.status
      : canEvaluate
        ? !props.widget?.data?.dataset_id
          ? "missing"
          : props.status !== "ready"
            ? props.status
            : "loading"
        : props.widget?.kpi_calculation
          ? "unavailable"
          : props.status === "ready"
            ? dashboardKpiDataStatus(props.table, props.data)
            : props.status
  const total =
    status === "ready"
      ? localFormula
        ? localFormula.value
        : calculated && canEvaluate
        ? calculated.value
        : aggregateDashboardKpi(props.table, props.data)
      : null
  // Bobur (6563385): a KPI may carry its unit in a column (`unit_field`, e.g. "QAR"). It becomes part of the
  // formatted value, so the real-width fit below measures the whole displayed string, unit included.
  const fieldUnits = props.data?.unit_field
    ? [...new Set((props.table?.rows || []).map((row) => row[props.data.unit_field]).filter(Boolean).map(String))]
    : []
  // The leshabank report agent writes valueFormat "float" (Bobur's formatter read anything unknown as a number);
  // main's validator only knows number/integer/currency/percent, so map the agent's aliases first.
  const valueFormat = KPI_FORMAT_ALIASES[props.style?.valueFormat] ?? props.style?.valueFormat
  const displayStyle = {
    ...props.style,
    ...(valueFormat !== props.style?.valueFormat ? { valueFormat } : {}),
    ...(!props.style?.unit && fieldUnits.length === 1 && Array.from(fieldUnits[0]).length <= 24 ? { unit: fieldUnits[0] } : {}),
  }
  const resolved = total === null ? undefined : resolveDashboardKpi(total, displayStyle, props.locale)
  const result =
    resolved?.status === "ready" && displayStyle.prefix
      ? { ...resolved, displayValue: withKpiPrefix(resolved.displayValue, displayStyle) }
      : resolved
  // Artyom: a client factsheet shows the exact amount ("27,486,006.97 QAR"). K/M/B only when the
  // widget's own format asks for it (notation / scale); never chosen automatically.
  // The unit can move to a second, smaller line when the number and unit do not fit side by side.
  const unitText = String(displayStyle.unit || "").trim()
  const numberText =
    result?.status === "ready" && unitText && result.displayValue.endsWith(` ${unitText}`)
      ? result.displayValue.slice(0, -unitText.length - 1)
      : undefined
  const fit = useContext(DashboardFitContext)
  // The fit this render computed, reported to the KPI row after render (hooks stay above the early returns).
  const latestFit = useRef(null)
  const reportedFit = useRef(undefined)
  latestFit.current = null
  useEffect(() => {
    if (!fit.reportKpiFit || !props.widget?.id) return
    const next = latestFit.current
    const previous = reportedFit.current
    if (next ? previous && previous.full === next.full : !previous) return
    reportedFit.current = next
    fit.reportKpiFit(props.widget.id, next)
  })
  const measure = () => {
    const value = element.current?.querySelector(".datox-react-dashboard__kpi-value")
    const parent = element.current?.parentElement
    if (!value || !parent) return
    // Measured in the DOM at 100px with the value's own styles: a canvas measureText has no
    // tabular figures, so "27,651,964.79 QAR" read ~5% narrower than it renders, "fit", and then
    // wrapped to two lines and clipped at the top of the card (Hamad p1).
    const probe = document.createElement("span")
    probe.setAttribute("aria-hidden", "true")
    probe.style.cssText = "position:absolute;left:0;top:0;visibility:hidden;white-space:nowrap;font-size:100px;line-height:1;pointer-events:none"
    value.appendChild(probe)
    const em = (text) => {
      if (!text) return 0
      probe.textContent = text
      return probe.offsetWidth / 100
    }
    const textWidth = em(result?.displayValue ?? value.textContent)
    const numberWidth = em(numberText)
    probe.remove()
    // Layout px (clientWidth/Height), never getBoundingClientRect: the canvas zoom is a CSS transform,
    // and a KPI measured at 66% kept a 66%-sized value after switching to 100%.
    const padding = getComputedStyle(parent)
    const width = parent.clientWidth - parseFloat(padding.paddingLeft) - parseFloat(padding.paddingRight)
    const box = parent.clientHeight
    const height = box - parseFloat(padding.paddingTop) - parseFloat(padding.paddingBottom)
    setSize((current) =>
      current.width === width &&
      current.height === height &&
      current.box === box &&
      current.textWidth === textWidth &&
      current.numberWidth === numberWidth
        ? current
        : { width, height, box, textWidth, numberWidth },
    )
  }
  useEffect(() => {
    const parent = element.current?.parentElement
    if (!parent || typeof ResizeObserver === "undefined") return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(parent)
    void document.fonts?.ready.then(measure)
    return () => observer.disconnect()
  }, [status, result?.status])
  useEffect(measure, [status, result?.status, result?.displayValue, numberText, props.locale, props.font])
  // Nothing to show (not connected, or no rows for this client): the shared empty state (Batch 3).
  if (status === "missing")
    return <WidgetEmpty kpi labels={props.labels} reason={props.widget?.data?.dataset_id ? "noValues" : "notConnected"} />
  if (status !== "ready")
    return (
      <div
        className="datox-react-dashboard__kpi-state"
        role={status === "error" || status === "invalid" ? "alert" : "status"}
        aria-busy={status === "loading"}
        data-kpi-state={status}
      >
        {status === "invalid" ? kpiIssueMessage(labels, calculated?.error) : labels[status] || labels.missing}
      </div>
    )
  if (result.status !== "ready")
    return (
      <div className="datox-react-dashboard__kpi-state" role="alert" data-kpi-state="error">
        {Number.isFinite(total) ? labels.invalid : labels.error}
      </div>
    )
  const availableWidth = Math.max(80, size.width || (props.layout.w || 320) - 52)
  const availableHeight = size.height || Math.max(14, (props.layout.h || 150) - 42)
  // Summer KPI composition (KpiStat): label (in the frame head), value, then a sublabel or a delta.
  // Small cards drop the sublabel first, before the value has to shrink.
  const cardWidth = props.layout.w || 320
  const cardHeight = props.layout.h || 150
  const roomForSublabel = kpiShowsSublabel({
    cardWidth,
    cardHeight,
    hasSublabel: !!props.sublabel,
    hasTarget: !!result.target,
    hasDelta: !!props.comparison,
    printing: !!props.printing,
  })
  const sublabelSize = KPI_COMPOSITION.sublabelPx
  const sublabelLines = kpiSublabelLines(cardHeight)
  // The sublabel may sit in part of the body's bottom padding (it is small, muted text).
  const sublabelBleed =
    size.box > 0 ? Math.min(KPI_COMPOSITION.sublabelBleedPx, Math.max(0, size.box - size.height - 4)) : 0
  const sublabelReserve =
    Math.ceil(sublabelSize * KPI_COMPOSITION.labelLineHeight * sublabelLines) + KPI_COMPOSITION.gapPx - sublabelBleed
  // Main's real-width fit (the unit is inside the measured string), clamped to the summer 12-104px range
  // by the widget height as well as the width.
  const baseFitInput = {
    cardHeight,
    bodyWidth: availableWidth,
    bodyHeight: availableHeight,
    bodyBoxHeight: size.box,
    hasTarget: !!result.target,
    sublabelPx: 0,
    layout: result.layout,
    fontScale: props.fontScale,
  }
  const valueText = { textEm: size.textWidth, chars: result.displayValue.length }
  // The caveat is dropped first, but only when it would cost the value more than a fifth of its
  // size (a two-line label on a narrow card); the PDF always keeps it.
  const showSublabel =
    roomForSublabel &&
    (!!props.printing ||
      kpiValueSize({ ...baseFitInput, ...valueText, sublabelPx: sublabelReserve }) >=
        KPI_COMPOSITION.sublabelKeepRatio * kpiValueSize({ ...baseFitInput, ...valueText }))
  const fitInput = { ...baseFitInput, sublabelPx: showSublabel ? sublabelReserve : 0 }
  // Each KPI gets the largest size that fits its own card (no shared row size, which forced a long
  // amount to shrink everyone or to compact).
  const fullFit = kpiValueSize({ ...fitInput, textEm: size.textWidth, chars: result.displayValue.length })
  // Even the minimum size cannot fit "number unit" on one line: the unit goes to a smaller line below.
  const minPx = KPI_VALUE_FIT.minPx
  const fullFits = !size.textWidth || size.textWidth * fullFit <= availableWidth + 0.5
  const splitUnit = !!numberText && !fullFits && fullFit <= minPx
  const valueSize = splitUnit
    ? kpiValueSize({ ...fitInput, bodyHeight: availableHeight * 0.72, textEm: size.numberWidth, chars: numberText.length })
    : fullFit
  if (size.box > 0) latestFit.current = { full: valueSize }
  // Baselines across the row: the value line uses the row's largest size as its strut, so every
  // value in the row sits on one baseline at the body bottom.
  const rowKey = fit.kpiRows?.[props.widget?.id]
  const rowSize = rowKey
    ? kpiRowSize(
        Object.entries(fit.kpiRows)
          .filter(([, key]) => key === rowKey)
          .map(([id]) => fit.kpiFits?.[id]),
      )
    : undefined
  const strutSize = splitUnit ? valueSize : Math.max(valueSize, rowSize || 0)
  const scaled = (px) => (props.valueScale === undefined ? px : Math.max(8, (px * props.valueScale) / 100))
  const target = result.target
  return (
    <div
      ref={element}
      className={`datox-react-dashboard__kpi datox-react-dashboard__kpi--${result.layout}`}
      style={{ textAlign: props.style.textAlign || "left" }}
      data-kpi-state="ready"
    >
      <div
        className="datox-react-dashboard__kpi-value"
        aria-label={labels.actual}
        style={{ fontSize: scaled(strutSize) }}
        title={result.displayValue}
        data-kpi-split-unit={splitUnit ? "" : undefined}
      >
        <span className="datox-react-dashboard__kpi-number" style={{ fontSize: scaled(valueSize) }}>
          {splitUnit ? numberText : result.displayValue}
        </span>
      </div>
      {splitUnit ? (
        <div className="datox-react-dashboard__kpi-unit" style={{ fontSize: Math.max(minPx, Math.round(scaled(valueSize) * 0.6)) }}>
          {unitText}
        </div>
      ) : null}
      {showSublabel ? (
        <div
          className="datox-react-dashboard__kpi-sublabel"
          style={{
            fontSize: sublabelSize,
            WebkitLineClamp: sublabelLines,
            lineClamp: sublabelLines,
            marginBottom: sublabelBleed ? -sublabelBleed : undefined,
          }}
          title={props.sublabel}
        >
          {props.sublabel}
        </div>
      ) : null}
      {target ? (
        <div className="datox-react-dashboard__kpi-target">
          <div className="datox-react-dashboard__kpi-target-label">
            {labels.target}: {target.displayValue}
          </div>
          <div
            className="datox-react-dashboard__kpi-progress"
            role="progressbar"
            aria-label={labels.progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={target.fill}
            aria-valuetext={`${labels.actual}: ${result.displayValue}; ${labels.target}: ${target.displayValue}; ${target.text}`}
          >
            {target.visual === "ring" ? (
              <svg className="datox-react-dashboard__kpi-ring" viewBox="0 0 40 40" aria-hidden="true">
                <circle cx="20" cy="20" r="16" fill="none" stroke="#e4e7ec" strokeWidth="4" />
                <circle
                  cx="20"
                  cy="20"
                  r="16"
                  fill="none"
                  stroke={props.accent || "#2f80ed"}
                  strokeWidth="4"
                  strokeDasharray={`${(target.fill * Math.PI * 32) / 100} ${Math.PI * 32}`}
                  transform="rotate(-90 20 20)"
                />
              </svg>
            ) : (
              <div className="datox-react-dashboard__kpi-bar" aria-hidden="true">
                <div style={{ width: `${target.fill}%`, backgroundColor: props.accent || "#2f80ed" }} />
              </div>
            )}
            <span>{target.text}</span>
          </div>
        </div>
      ) : null}
      {props.comparison ? (
        <KpiComparisonDisplay labels={props.labels} widget={props.widget} result={props.comparison} />
      ) : null}
    </div>
  )
}

export function KpiDisplaySettings(props) {
  const labels = props.labels.kpi
  const style = props.style
  const percent = style.valueFormat === "percent"
  const scaling = style.scale && style.scale !== "none" ? style.scale : style.notation || "standard"
  const target = style.target
  return (
    <>
      <label>
        <span>{props.labels.valueFormat}</span>
        <select
          className="datox-react-dashboard__select"
          value={style.valueFormat || "number"}
          onChange={(event) => props.onChange({ valueFormat: event.currentTarget.value })}
        >
          <option value="number">{props.labels.numberFormat}</option>
          <option value="integer">{props.labels.integerFormat}</option>
          <option value="currency">{labels.currencyFormat}</option>
          <option value="percent">{props.labels.percentFormat}</option>
        </select>
      </label>
      <label>
        <span>{props.labels.decimalPlaces}</span>
        <input
          className="datox-react-dashboard__input"
          type="number"
          min="0"
          max="6"
          step="1"
          disabled={style.valueFormat === "integer"}
          value={style.valueFormat === "integer" ? 0 : (style.decimalPlaces ?? 2)}
          onChange={(event) =>
            props.onChange({ decimalPlaces: Math.min(6, Math.max(0, Math.trunc(Number(event.currentTarget.value)))) })
          }
        />
      </label>
      {style.valueFormat === "currency" ? (
        <label>
          <span>{labels.currency}</span>
          <input
            className="datox-react-dashboard__input"
            maxLength={3}
            value={style.currency ?? "USD"}
            onChange={(event) => props.onChange({ currency: event.currentTarget.value.toUpperCase() })}
          />
        </label>
      ) : null}
      {percent ? (
        <label>
          <span>{labels.percentScale}</span>
          <select
            className="datox-react-dashboard__select"
            value={style.percentScale || "points"}
            onChange={(event) => props.onChange({ percentScale: event.currentTarget.value })}
          >
            <option value="points">{labels.points}</option>
            <option value="ratio">{labels.ratio}</option>
          </select>
        </label>
      ) : null}
      <label>
        <span>{labels.unit}</span>
        <input
          className="datox-react-dashboard__input"
          maxLength={24}
          value={style.unit || ""}
          onChange={(event) => props.onChange({ unit: event.currentTarget.value })}
        />
      </label>
      <label>
        <span>{labels.notation}</span>
        <select
          className="datox-react-dashboard__select"
          disabled={percent}
          value={percent ? "standard" : scaling}
          onChange={(event) =>
            props.onChange(
              ["standard", "compact"].includes(event.currentTarget.value)
                ? { notation: event.currentTarget.value, scale: "none" }
                : { notation: "standard", scale: event.currentTarget.value },
            )
          }
        >
          <option value="standard">{labels.standard}</option>
          <option value="compact">{labels.compact}</option>
          <option value="thousands">{labels.thousands}</option>
          <option value="millions">{labels.millions}</option>
          <option value="billions">{labels.billions}</option>
        </select>
      </label>
      <label>
        <span>{labels.layout}</span>
        <select
          className="datox-react-dashboard__select"
          value={style.kpiLayout || "standard"}
          onChange={(event) => props.onChange({ kpiLayout: event.currentTarget.value })}
        >
          <option value="standard">{labels.standard}</option>
          <option value="compact">{labels.compact}</option>
          <option value="prominent">{labels.prominent}</option>
        </select>
      </label>
      <label>
        <span>{labels.target}</span>
        <select
          className="datox-react-dashboard__select"
          value={target?.visual || "none"}
          onChange={(event) =>
            props.onChange({
              target:
                event.currentTarget.value === "none"
                  ? null
                  : { value: target?.value ?? 1, visual: event.currentTarget.value },
            })
          }
        >
          <option value="none">{labels.none}</option>
          <option value="bar">{labels.bar}</option>
          <option value="ring">{labels.ring}</option>
        </select>
      </label>
      {target ? (
        <label>
          <span>{labels.targetValue}</span>
          <small>{labels.targetHelp}</small>
          <input
            className="datox-react-dashboard__input"
            type="number"
            step="any"
            min="0"
            aria-invalid={!Number.isFinite(target.value) || target.value <= 0}
            value={target.value ?? ""}
            onChange={(event) =>
              props.onChange({
                target: {
                  ...target,
                  value: event.currentTarget.value === "" ? null : Number(event.currentTarget.value),
                },
              })
            }
          />
        </label>
      ) : null}
      {resolveDashboardKpi(0, style).status === "invalid" ? <p role="alert">{labels.invalid}</p> : null}
    </>
  )
}

export function KpiFormulaSettings(props) {
  const kpi = useContext(DashboardKpiContext)
  const labels = props.labels.kpi
  const saved = props.widget.kpi_calculation
  const [mode, setMode] = useState(saved ? "formula" : "aggregation")
  const [expression, setExpression] = useState(saved?.expression || "")
  const [preview, setPreview] = useState(null)
  const key = dashboardKpiRequestKey(
    {
      ...props.widget,
      kpi_calculation: mode === "formula" ? { version: 1, mode: "formula", expression } : null,
    },
    props.filterWidgets || [],
    kpi.sourceKeys?.[props.widget.data?.dataset_id] ?? kpi.sourceKey,
  )
  useEffect(() => {
    setMode(saved ? "formula" : "aggregation")
    setExpression(saved?.expression || "")
  }, [props.widget.id, saved?.expression, kpi.dashboardID])
  useEffect(() => {
    if ((mode === "formula" && !expression.trim()) || !kpi.onEvaluateKpi) return undefined
    let active = true
    setPreview(null)
    const timer = setTimeout(() => {
      kpi
        .onEvaluateKpi(
          { ...props.widget, kpi_calculation: mode === "formula" ? { version: 1, mode: "formula", expression } : null },
          props.filterWidgets || [],
          0,
        )
        .then((result) => {
          if (active) setPreview({ key, result })
        })
        .catch(() => {
          if (active) setPreview({ key, result: { status: "error" } })
        })
    }, 300)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [key, mode])
  // Without a server evaluator the preview runs on Bobur's client interpreter, over the widget's table.
  const localResult =
    !kpi.onEvaluateKpi && props.table && !(mode === "formula" && !expression.trim())
      ? (() => {
          const local = kpiResult(
            { ...props.widget, kpi_calculation: mode === "formula" ? { version: 1, mode: "formula", expression } : null },
            props.table,
          )
          return local.status === "ready" && typeof local.value === "number"
            ? { status: "ready", value: local.value }
            : { status: local.status === "error" ? "error" : "unavailable" }
        })()
      : null
  const result = localResult || (preview?.key === key ? preview.result : null)
  const previewText =
    result?.status === "ready"
      ? withKpiPrefix(
          resolveDashboardKpi(result.value, kpiDisplayStyle(props.widget), kpi.locale).displayValue,
          kpiDisplayStyle(props.widget),
        ) || labels.invalid
      : result?.status === "invalid"
        ? kpiIssueMessage(labels, result.error)
        : result?.status === "unavailable"
          ? labels.unavailable
          : result?.status === "error"
            ? labels.error
            : mode === "formula" && !expression.trim()
              ? labels.expression_required
              : labels.loading
  const previewDisplay = (
    <div role={result?.status === "invalid" || result?.status === "error" ? "alert" : "status"}>
      {labels.preview}: {previewText}
    </div>
  )
  return (
    <div className="datox-react-dashboard__chart-data-editor">
      <label>
        <span>{labels.calculation}</span>
        <select
          className="datox-react-dashboard__select"
          value={mode}
          onChange={(event) => {
            const next = event.currentTarget.value
            setMode(next)
            if (next === "aggregation" && saved) props.onChange(null)
          }}
        >
          <option value="aggregation">{labels.aggregation}</option>
          <option value="formula">{labels.formula}</option>
        </select>
      </label>
      {mode === "formula" ? (
        <>
          <label>
            <span>{labels.expression}</span>
            <textarea
              className="datox-react-dashboard__input"
              maxLength={1000}
              value={expression}
              onChange={(event) => setExpression(event.currentTarget.value)}
            />
          </label>
          <small>{labels.formulaHelp}</small>
          {previewDisplay}
          <button
            type="button"
            className="datox-react-dashboard__calculation-button"
            disabled={!result || !["ready", "unavailable"].includes(result.status) || expression === saved?.expression}
            onClick={() => props.onChange({ version: 1, mode: "formula", expression })}
          >
            {labels.apply}
          </button>
        </>
      ) : (
        <>
          {props.children}
          {previewDisplay}
        </>
      )}
    </div>
  )
}
